import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VerificationModal from '../../../components/student/verificationModal/VerificationModal';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  XCircle, 
  FileBarChart, 
  History,
  ChevronRight,
  X,
  Upload
} from 'lucide-react';
import '../../../assets/css/student/dashboard/studentdashboard.css';
import StudentLayout from "../../../layouts/student/StudentLayout";

const DonationDashboard = () => {
  const navigate = useNavigate();
  
  // Modal states
  const [showVerification, setShowVerification] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    donationType: '',
    amount: '',
    reason: '',
    file: null
  });

  const stats = [
    { title: 'Total Requests', value: '5', icon: FileText, className: 'sdd-stat-icon-gray' },
    { title: 'Approved Donations', value: '3', icon: CheckCircle, className: 'sdd-stat-icon-orange' },
    { title: 'Pending Requests', value: '2', icon: Clock, className: 'sdd-stat-icon-orange' },
    { title: 'Rejected Requests', value: '1', icon: XCircle, className: 'sdd-stat-icon-orange' }
  ];

  const recentRequests = [
    { title: 'Books Assistance', status: 'Pending', type: 'Books for Study', reason: 'Need textbooks for semester', amount: '5000' },
    { title: 'School Fee Support', status: 'Approved', type: 'College Fees', reason: 'Financial difficulties', amount: '15000' },
    { title: 'Uniform Help', status: 'Rejected', type: 'School Uniform', reason: 'Uniform replacement', amount: '3000' }
  ];

  const notifications = [
    'Document Verified Successfully',
    'Donation Request Approved by NGO',
    'New Message from Support Team'
  ];

  const handleViewAllClick = (e) => {
    e.preventDefault();
    navigate('/student/myrequests');
  };

  // Modal handlers
  const handleApplyClick = () => {
    setSelectedRequest(null);
    setFormData({
      donationType: '',
      amount: '',
      reason: '',
      file: null
    });
    setIsModalOpen(true);
  };

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      file: file
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Add your API call here
    handleCloseModal();
  };
  useEffect(() => {
  const isVerified = localStorage.getItem("student_verified");

  if (!isVerified) {
    setShowVerification(true);
  }
}, []);

  return (
    <StudentLayout>
      <div className="sdd-dashboard-wrapper">
        <div className="sdd-dashboard-container">

          {/* Stats Cards */}
          <div className="sdd-stats-grid">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="sdd-stat-card">
                  <div className="sdd-stat-card-content">
                    <div className={`sdd-stat-icon ${stat.className}`}>
                      <Icon size={24} />
                    </div>
                    <div className="sdd-stat-info">
                      <p className="sdd-stat-title">{stat.title}</p>
                      <h3 className="sdd-stat-value">{stat.value}</h3>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recent Donation Requests */}
          <div className="sdd-content-card sdd-requests-section">
            <div className="sdd-card-header">
              <h5 className="sdd-card-title">Recent Donation Requests</h5>
              <a 
                href="#" 
                className="view-all-link" 
                onClick={handleViewAllClick}
              >
                View All <ChevronRight size={16} />
              </a>
            </div>

            <ul className="sdd-request-list">
              {recentRequests.map((request, index) => (
                <li 
                  key={index} 
                  className="sdd-request-item"
                  onClick={() => handleRequestClick(request)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="sdd-request-info">
                    <h6>{request.title}</h6>
                    <p>- {request.status}</p>
                  </div>
                  <span className="sdd-status-badge">{request.status}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Notifications */}
          <div className="sdd-content-card sdd-notifications-section">
            <div className="sdd-card-header">
              <h5 className="sdd-card-title">Latest Notifications</h5>
              <a href="#" className="sdd-view-all-link">
                View All <ChevronRight size={16} />
              </a>
            </div>

            <ul className="sdd-notification-list">
              {notifications.map((notification, index) => (
                <li key={index} className="sdd-notification-item">
                  <CheckCircle size={20} className="sdd-notification-icon" />
                  <span className="sdd-notification-text">{notification}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="sdd-action-buttons">
            <button 
              className="sdd-action-btn sdd-action-btn-primary"
              onClick={handleApplyClick}
            >
              <FileBarChart size={20} className="sdd-btn-icon" />
              Apply for Donation
            </button>

            <button
              className="sdd-action-btn sdd-action-btn-secondary"
              onClick={() => navigate("/student/history")}
            >
              <History size={20} className="sdd-btn-icon" />
              View Donation History
            </button>
          </div>

        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="donation-modal-overlay" onClick={handleCloseModal}>
          <div className="donation-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="donation-modal-header">
              <h3>{selectedRequest ? `Request Details - ${selectedRequest.type}` : 'Apply for Donation'}</h3>
              <button className="donation-modal-close-btn" onClick={handleCloseModal}>
                <X size={24} />
              </button>
            </div>

            <div className="donation-modal-body">
              {selectedRequest ? (
                <div className="donation-request-details">
                  <div className="donation-detail-row">
                    <strong>Request Type:</strong>
                    <span>{selectedRequest.type}</span>
                  </div>
                  <div className="donation-detail-row">
                    <strong>Amount:</strong>
                    <span>Rs. {selectedRequest.amount}</span>
                  </div>
                  <div className="donation-detail-row">
                    <strong>Reason:</strong>
                    <span>{selectedRequest.reason}</span>
                  </div>
                  <div className="donation-detail-row">
                    <strong>Status:</strong>
                    <span className={`donation-status-${selectedRequest.status.toLowerCase()}`}>
                      {selectedRequest.status}
                    </span>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="donation-apply-form">
                  <div className="donation-form-group">
                    <label htmlFor="donationType">Donation Type</label>
                    <select
                      id="donationType"
                      name="donationType"
                      value={formData.donationType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="College Fees">College Fees</option>
                      <option value="Books for Study">Books for Study</option>
                      <option value="School Uniform">School Uniform</option>
                    </select>
                  </div>

                  <div className="donation-form-group">
                    <label htmlFor="amount">Amount</label>
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      placeholder="Enter amount"
                      required
                    />
                  </div>

                  <div className="donation-form-group">
                    <label htmlFor="reason">Reason / Description</label>
                    <textarea
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      placeholder="Explain your reason"
                      rows="4"
                      required
                    />
                  </div>

                  <div className="donation-form-group">
                    <label htmlFor="file">Upload Documents</label>
                    <div className="donation-file-upload-wrapper">
                      <input
                        type="file"
                        id="file"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                      <label htmlFor="file" className="donation-file-upload-label">
                        <Upload size={20} />
                        {formData.file ? formData.file.name : 'Choose file'}
                      </label>
                    </div>
                  </div>

                  <div className="donation-modal-footer">
                    <button type="submit" className="donation-btn-submit">
                      Submit
                    </button>
                    <button type="button" className="donation-btn-cancel" onClick={handleCloseModal}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              {/* ✅ Student Verification Modal */}
               {showVerification && (
                <VerificationModal
                onClose={() => setShowVerification(false)}
                   />
                  )}
            </div>
          </div>
        </div>
            
      )}
    </StudentLayout>
  );
};

export default DonationDashboard;